import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import ChatBubble from './ChatBubble';
import ChatInput from './ChatInput';
import ConfirmationPrompt from './ConfirmationPrompt';
import ProactivityPrompt from './ProactivityPrompt';
import { useAuth } from '../Auth';
import { useNexusSync } from '../../context/NexusSyncContext';
import { Loader2 } from 'lucide-react';

const AUTOMATION_POLL_INTERVAL_MS = 8000;

const greetingPool = [
  'Salut ! On se fait un point rapide ?',
  'Hey ! Besoin d’un coup de main ?',
  'Coucou, je suis prêt à faire avancer tes idées.',
  'On s’y met ? Dis-moi ce qui t’intéresse.',
  'Hello ! Partant pour éclaircir ta to-do ?',
  'Content de te revoir ! On commence par quoi ?',
  'Nexus au rapport. Tu veux analyser ou agir ?',
  'Je suis là, prêt à explorer ce qui t’occupe.',
  'Besoin d’un check express ? Je peux m’en charger.',
  'On décortique une question ? Je suis dispo.',
  'Yo ! On passe en mode action ?',
  'Prêt quand tu l’es. On s’attele à ton prochain sujet ?',
  'On aligne nos idées ? Lance la première.',
  'Salut ! Tu veux un résumé, une décision, un plan ?',
  'Je peux creuser n’importe quelle piste, dis-moi laquelle.',
  'Envie de clarifier quelque chose ? Je t’écoute.',
  'On se met en route, tu me briefe ?',
  'Nexus en ligne. On dynamise ta prochaine action ?',
  'Un doute, une idée, une question : je suis sur le pont.',
  'C’est parti ! Tu veux qu’on commence par quoi ?',
];

const getRandomGreeting = () => ({
  content: greetingPool[Math.floor(Math.random() * greetingPool.length)],
  role: 'assistant',
  agent: 'nexus',
});

const generateConversationId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `conv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const formatHourMinute = (date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatAutomationTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const normalizeAutomationMetadata = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return raw;
};

const buildAutomationMessageContent = (run) => {
  const metadata = normalizeAutomationMetadata(run.metadata);
  const response = (run.response_message || '').trim();
  const metadataMessage = (metadata.message || '').trim();
  const fallbackPrompt = (metadata.prompt || '').trim();
  const condition = (metadata.condition || '').trim();
  const baseText = response || metadataMessage || fallbackPrompt || "Nexus n'a pas fourni de message.";
  const lines = [];

  if (run.status === 'error') {
    const errorText = (run.error_message || '').trim() || "L'automatisation n'a pas abouti.";
    lines.push(`⚠️ ${errorText}`);
    if (baseText && baseText !== errorText) {
      lines.push(baseText);
    }
  } else if (baseText) {
    lines.push(baseText);
  }

  if (condition) {
    lines.push(`_(Condition suivie : ${condition})_`);
  }

  const footerParts = [];
  if (run.automation_title) {
    footerParts.push(`automatisation « ${run.automation_title} »`);
  }
  const timestampLabel = formatAutomationTimestamp(run.triggered_at);
  if (timestampLabel) {
    footerParts.push(`déclenchée le ${timestampLabel}`);
  }
  if (footerParts.length) {
    lines.push(`_${footerParts.join(' · ')}_`);
  }

  return lines.filter(Boolean).join('\n\n');
};

const buildPageContext = ({ pathname, search }) => {
  const filters = {};
  if (search) {
    const params = new URLSearchParams(search);
    params.forEach((value, key) => {
      if (filters[key]) {
        filters[key] = Array.isArray(filters[key]) ? [...filters[key], value] : [filters[key], value];
      } else {
        filters[key] = value;
      }
    });
  }

  let description = 'Vue principale';
  const extra = {};
  let objectiveView = null;

  const projectMatch = pathname.match(/^\/project\/(\d+)/);
  if (projectMatch) {
    const projectId = Number(projectMatch[1]);
    description = 'Page projet';
    if (filters.panel) {
      extra.activePanel = filters.panel;
    }
    extra.projectId = Number.isNaN(projectId) ? projectMatch[1] : projectId;
  } else if (pathname === '/objectives') {
    objectiveView = filters.view === 'myObjectives' ? 'myObjectives' : 'project';
    description =
      objectiveView === 'myObjectives' ? 'Vue Mes objectifs' : 'Vue Objectifs par projet';
  } else if (pathname === '/projects' || pathname === '/') {
    description = 'Liste des projets';
  } else if (pathname === '/slots' || pathname.startsWith('/slots')) {
    description = 'Gestion des slots';
  } else if (pathname === '/hours') {
    description = 'Gestion des heures';
  } else if (pathname === '/expenses') {
    description = 'Vue dépenses';
  } else if (pathname === '/billing-batches') {
    description = 'Vue facturation';
  } else if (pathname === '/backlog') {
    description = 'Backlog global';
  }

  if (objectiveView) {
    extra.objectivesView = objectiveView;
  }

  let sanitizedFilters = { ...filters };
  if (objectiveView === 'myObjectives') {
    sanitizedFilters = {};
    if (filters.view) {
      sanitizedFilters.view = filters.view;
    }
  }

  return {
    pathname,
    description,
    filters: sanitizedFilters,
    extra,
    timestamp: new Date().toISOString(),
  };
};

const formatActivityDetails = (details) => {
  if (!details) return '';
  if (typeof details === 'string') return details;
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
};

const activityStatusClasses = {
  running: 'bg-blue-50 text-blue-700 border-blue-100',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  waiting: 'bg-amber-50 text-amber-700 border-amber-100',
  error: 'bg-red-50 text-red-700 border-red-100',
};

const resolveActivityType = (phase) => {
  if (phase === 'tool_result') return 'tool_result';
  if (phase === 'tool_call') return 'tool_call';
  if (phase === 'routing') return 'routing';
  if (phase === 'confirmation') return 'confirmation';
  return 'tool_call';
};

const ActivityTimeline = ({ entries }) => (
  <div className="space-y-4">
    {entries.length === 0 ? (
      <div className="flex flex-col items-start gap-1 rounded-2xl border border-indigo-100/60 bg-white/60 px-4 py-5">
        <span className="text-sm font-medium text-indigo-500">Trace outils</span>
        <p className="text-xs text-indigo-900/70">
          Les appels d'outils et activités de Nexus seront listés ici pendant la conversation.
        </p>
      </div>
    ) : (
      entries.map((entry) => (
        <div key={entry.id} className="relative rounded-2xl border border-indigo-100/80 bg-white/80 p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between gap-2 text-xs uppercase tracking-wide text-indigo-400">
            <div className="flex min-w-0 items-center gap-2">
              <span>{entry.typeLabel}</span>
              {entry.status && (
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal ${
                    activityStatusClasses[entry.status] || 'border-indigo-100 bg-indigo-50 text-indigo-600'
                  }`}
                >
                  {entry.status}
                </span>
              )}
            </div>
            <span>{formatHourMinute(entry.timestamp)}</span>
          </div>
          <p className="text-sm font-semibold text-indigo-900">{entry.text}</p>
          {entry.tool && (
            <p className="mt-1 break-words font-mono text-[11px] text-indigo-500">{entry.tool}</p>
          )}
          {entry.details && (
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-indigo-50/70 p-3 text-xs leading-relaxed text-indigo-900/80">
              {formatActivityDetails(entry.details)}
            </pre>
          )}
        </div>
      ))
    )}
  </div>
);

const TypingIndicator = ({ focus, entries = [] }) => {
  const latestStartIndex = entries.reduce(
    (latestIndex, entry, index) => (entry.type === 'start' ? index : latestIndex),
    -1
  );
  const currentEntries = latestStartIndex >= 0 ? entries.slice(latestStartIndex) : entries;
  const visibleEntries = currentEntries.filter((entry) => entry.type !== 'completed').slice(-3);
  const latest = visibleEntries[visibleEntries.length - 1];
  const title = latest?.typeLabel || 'Nexus travaille';
  const detail = focus || latest?.text || 'Initialisation du routage Nexus';
  return (
    <div className="flex gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3 text-indigo-600">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
      </div>
      <div className="flex-1">
        <p className="text-xs font-semibold uppercase text-indigo-400">
          {title}
        </p>
        <p className="text-sm font-medium text-indigo-900">{detail}</p>
        {visibleEntries.length > 0 && (
          <div className="mt-3 space-y-2">
            {visibleEntries.map((entry) => (
              <div key={`typing-${entry.id}`} className="rounded-xl border border-indigo-100 bg-white/70 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-xs font-semibold text-indigo-700">{entry.text}</span>
                  {entry.status && (
                    <span className="shrink-0 text-[10px] uppercase text-indigo-400">
                      {entry.status}
                    </span>
                  )}
                </div>
                {entry.tool && (
                  <p className="mt-1 truncate font-mono text-[11px] text-indigo-500">{entry.tool}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatBot = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([getRandomGreeting()]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [currentFocus, setCurrentFocus] = useState('');
  const [conversationId, setConversationId] = useState(() => generateConversationId());
  const [clearingConversation, setClearingConversation] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [bugReportInitiating, setBugReportInitiating] = useState(false);
  const [proactivityRequests, setProactivityRequests] = useState([]);
  const [proactivityLoading, setProactivityLoading] = useState(false);
  const [respondingProactivityId, setRespondingProactivityId] = useState(null);
  const [automationUnread, setAutomationUnread] = useState(0);
  const [nexusAlertPending, setNexusAlertPending] = useState(false);
  const messagesEndRef = useRef(null);
  const readerRef = useRef(null);
  const pendingConfirmationRef = useRef(null);
  const conversationIdRef = useRef(conversationId);
  const deepflowProjectRef = useRef({ id: null, promise: null });
  const messagesRef = useRef(messages);
  const activityLogRef = useRef(activityLog);
  const proactivityIntervalRef = useRef(null);
  const proactivityIdSetRef = useRef(new Set());
  const automationDeliveredRef = useRef(new Set());
  const automationAckPendingRef = useRef(new Set());
  const proactivityCountRef = useRef(0);
  const automationCountRef = useRef(0);
  const isOpenRef = useRef(isOpen);
  const { publishMutation, lastMutation } = useNexusSync();

  const updateAlertBadge = useCallback(() => {
    const hasItems = proactivityCountRef.current > 0 || automationCountRef.current > 0;
    if (!isOpenRef.current && hasItems) {
      setNexusAlertPending(true);
    } else if (isOpenRef.current && !hasItems) {
      setNexusAlertPending(false);
    }
  }, []);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    activityLogRef.current = activityLog;
  }, [activityLog]);

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      if (automationUnread !== 0) {
        automationCountRef.current = 0;
        setAutomationUnread(0);
      } else if (automationCountRef.current !== 0) {
        automationCountRef.current = 0;
      }
      updateAlertBadge();
    }
  }, [isOpen, automationUnread, updateAlertBadge]);

  const activityLabels = useMemo(
    () => ({
      start: 'Analyse',
      routing: 'Routage',
      tool_call: 'Appel outil',
      tool_result: 'Résultat outil',
      confirmation: 'Confirmation',
      completed: 'Réponse prête',
      error: 'Erreur',
    }),
    []
  );

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen) {
      setNexusAlertPending(false);
    }
  }, [isOpen]);

  useEffect(() => () => {
    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {});
    }
    if (proactivityIntervalRef.current) {
      clearInterval(proactivityIntervalRef.current);
    }
  }, []);

  const publishToolMutation = useCallback(
    (mutation) => {
      if (!publishMutation || !mutation) {
        return;
      }
      const metadata = mutation.metadata;
      if (!metadata || Object.keys(metadata).length === 0) {
        return;
      }
      publishMutation({
        source: 'nexus',
        tool: mutation.tool,
        metadata,
        summary: mutation.summary,
      });
    },
    [publishMutation]
  );

  const loadProactivityRequests = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (!silent) {
        setProactivityLoading(true);
      }
      try {
        const data = await apiService.getNexusProactivityRequests();
        const nextRequests = data?.requests ?? [];
        const nextIds = new Set(nextRequests.map((item) => item.id));
        proactivityIdSetRef.current = nextIds;
        setProactivityRequests(nextRequests);
        proactivityCountRef.current = nextRequests.length;
        updateAlertBadge();
      } catch (error) {
        console.warn('Impossible de charger les demandes proactives Nexus:', error);
      } finally {
        if (!silent) {
          setProactivityLoading(false);
        }
      }
    },
    [updateAlertBadge]
  );

  const appendAutomationRuns = useCallback((runs) => {
    if (!Array.isArray(runs) || runs.length === 0) {
      return;
    }
    setMessages((prev) => [
      ...prev,
      ...runs.map((run) => ({
        content: buildAutomationMessageContent(run),
        role: 'assistant',
        agent: 'nexus',
        automationRunId: run.id,
      })),
    ]);
  }, []);

  const acknowledgeAutomationRuns = useCallback(async (runs) => {
    if (!Array.isArray(runs) || runs.length === 0) {
      return;
    }
    await Promise.all(
      runs.map((run) =>
        apiService
          .acknowledgeAutomationMessage(run.id)
          .catch((error) => console.warn("Impossible d'accuser réception du message automatique:", error))
          .finally(() => {
            automationAckPendingRef.current.delete(run.id);
          })
      )
    );
  }, []);

  const loadAutomationMessages = useCallback(
    async () => {
      try {
        const data = await apiService.getAutomationMessages();
        const runs = Array.isArray(data) ? data : [];

        if (runs.length === 0) {
          if (isOpenRef.current && automationCountRef.current !== 0) {
            automationCountRef.current = 0;
            if (automationUnread !== 0) {
              setAutomationUnread(0);
            }
            updateAlertBadge();
          }
          return;
        }

        const newRuns = runs.filter((run) => !automationDeliveredRef.current.has(run.id));
        if (newRuns.length > 0) {
          newRuns.forEach((run) => {
            automationDeliveredRef.current.add(run.id);
            automationAckPendingRef.current.add(run.id);
          });
          appendAutomationRuns(newRuns);

          if (isOpenRef.current) {
            if (automationCountRef.current !== 0 || automationUnread !== 0) {
              automationCountRef.current = 0;
              setAutomationUnread(0);
            }
          } else {
            setAutomationUnread((prev) => {
              const next = prev + newRuns.length;
              automationCountRef.current = next;
              return next;
            });
          }
        }

        updateAlertBadge();

        const ackTargets = runs.filter((run) => automationAckPendingRef.current.has(run.id));
        if (ackTargets.length > 0) {
          await acknowledgeAutomationRuns(ackTargets);
        }
      } catch (error) {
        console.warn("Impossible de charger les messages d'automatisation Nexus:", error);
      }
    },
    [acknowledgeAutomationRuns, appendAutomationRuns, automationUnread, setAutomationUnread, updateAlertBadge]
  );

  useEffect(() => {
    if (!lastMutation || !lastMutation.metadata) {
      return;
    }
    const entity = (lastMutation.metadata.entity || '').toLowerCase();
    if (entity === 'tasks' || entity === 'objectives') {
      loadProactivityRequests({ silent: true });
      loadAutomationMessages();
    }
  }, [lastMutation, loadProactivityRequests, loadAutomationMessages]);

  useEffect(() => {
    const intervalMs = isOpen ? 30000 : 90000;
    if (proactivityIntervalRef.current) {
      clearInterval(proactivityIntervalRef.current);
    }
    const tick = () => {
      loadProactivityRequests({ silent: true });
    };
    tick();
    proactivityIntervalRef.current = setInterval(tick, intervalMs);
    return () => {
      if (proactivityIntervalRef.current) {
        clearInterval(proactivityIntervalRef.current);
        proactivityIntervalRef.current = null;
      }
    };
  }, [isOpen, loadProactivityRequests]);

  useEffect(() => {
    let timeoutId = null;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) {
        return;
      }
      await loadAutomationMessages();
      if (!cancelled) {
        timeoutId = setTimeout(poll, AUTOMATION_POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loadAutomationMessages]);

  useEffect(() => {
    loadProactivityRequests();
  }, [loadProactivityRequests]);

  useEffect(() => {
    const handler = () => {
      loadProactivityRequests({ silent: true });
      loadAutomationMessages();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('nexus-proactivity-refresh', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('nexus-proactivity-refresh', handler);
      }
    };
  }, [loadProactivityRequests, loadAutomationMessages]);

  const pushAssistantMessage = useCallback((content) => {
    if (!content) return;
    setMessages((prev) => [...prev, { content, role: 'assistant', agent: 'nexus' }]);
  }, [setMessages]);

  const ensureDeepflowProjectId = useCallback(async () => {
    if (deepflowProjectRef.current.id) {
      return deepflowProjectRef.current.id;
    }
    if (deepflowProjectRef.current.promise) {
      return deepflowProjectRef.current.promise;
    }
    const fetchPromise = apiService
      .get('/projects')
      .then((projects) => {
        const list = Array.isArray(projects) ? projects : [];
        const target = list.find((project) => project.name?.toLowerCase().includes('deepflow'));
        if (!target) {
          throw new Error("Impossible de trouver le projet Deepflow.");
        }
        deepflowProjectRef.current.id = target.id;
        return target.id;
      })
      .finally(() => {
        deepflowProjectRef.current.promise = null;
      });
    deepflowProjectRef.current.promise = fetchPromise;
    return fetchPromise;
  }, []);

  const buildConversationLog = useCallback(() => {
    const snapshot = messagesRef.current || [];
    const transcript = snapshot
      .filter(
        (msg) =>
          (msg.role === 'user' || msg.role === 'assistant') &&
          typeof msg.content === 'string' &&
          msg.content.trim()
      )
      .map((msg) => {
        const speaker = msg.role === 'user' ? 'Utilisateur' : 'Nexus';
        const agentSuffix = msg.agent && msg.agent !== 'nexus' ? ` (${msg.agent})` : '';
        return `${speaker}${agentSuffix}: ${msg.content.trim()}`;
      })
      .join('\n');
    return transcript;
  }, []);

  const buildActivityLogSummary = useCallback(() => {
    const snapshot = activityLogRef.current || [];
    if (!snapshot.length) return '';
    return snapshot
      .map((entry) => {
        const ts =
          entry.timestamp instanceof Date ? entry.timestamp : new Date(entry.timestamp || Date.now());
        const tsLabel = Number.isNaN(ts.getTime()) ? '' : ts.toLocaleString('fr-FR');
        const details = entry.details ? ` — ${entry.details}` : '';
        return `[${entry.typeLabel || entry.type || 'MCP'}${tsLabel ? ` · ${tsLabel}` : ''}] ${
          entry.text
        }${details}`;
      })
      .join('\n');
  }, []);

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen && messages.length === 0) {
      setMessages([getRandomGreeting()]);
    }
  };

  const clearPendingConfirmation = useCallback(() => {
    setPendingConfirmation(null);
    pendingConfirmationRef.current = null;
  }, []);

  const appendActivity = useCallback((type, text, details, meta = {}) => {
    setActivityLog((prev) => {
      const entry = {
        id: `${Date.now()}-${Math.random()}`,
        type,
        typeLabel: activityLabels[type] || 'Activité',
        text,
        details,
        tool: meta.tool || '',
        toolLabel: meta.toolLabel || '',
        status: meta.status || '',
        metadata: meta.metadata || null,
        timestamp: new Date(),
      };
      const next = [...prev, entry];
      return next.slice(-12);
    });
  }, [activityLabels]);

  const pageContext = useMemo(() => buildPageContext(location), [location]);


  const handleStreamEvent = useCallback((event) => {
    const { event: type, data } = event;
    if (!type || !data) return;

    if (type === 'activity') {
      const phase = data.phase || 'activity';
      const label = resolveActivityType(phase);
      const text = data.activity || data.tool_label || data.tool || 'Activité Nexus';
      appendActivity(label, text, data.details || '', {
        tool: data.tool,
        toolLabel: data.tool_label,
        status: data.status,
        metadata: data.metadata,
      });
      setCurrentFocus(text);
      if (phase === 'tool_result' && data.metadata) {
        publishToolMutation({
          tool: data.tool,
          metadata: data.metadata,
          summary: data.details || data.activity,
        });
      }
      return;
    }

    if (type === 'confirmation') {
      const details = (data.summary && data.summary.length && data.summary.join('\n')) || data.message;
      appendActivity('confirmation', 'Confirmation requise', details, {
        tool: data.tool,
        status: data.status || 'waiting',
      });
      const batchId = data.batch_id || data.call_id || `batch-${Date.now()}`;
      const actions = (data.actions || []).map((action) => ({
        tool: action.tool,
        title: action.title,
        summary: action.summary || [],
        callId: action.call_id,
        rawArguments: action.raw_arguments || action.arguments || {},
      }));
      const confirmation = {
        tool: data.tool,
        arguments: data.raw_arguments || data.arguments,
        callId: data.call_id,
        batchId,
        message: data.message,
        summary: data.summary || [],
        title: data.title || data.tool,
        locked: false,
        actions,
      };
      setPendingConfirmation(confirmation);
      pendingConfirmationRef.current = confirmation;
      setCurrentFocus('En attente de confirmation');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          agent: 'nexus',
          type: 'confirmation',
          confirmation,
        },
      ]);
      setLoading(false);
      return;
    }

    if (type === 'completed') {
      if (pendingConfirmationRef.current && !pendingConfirmationRef.current.locked) {
        setLoading(false);
        return;
      }
      const mutations = Array.isArray(data.mutations) ? data.mutations : [];
      if (mutations.length) {
        mutations.forEach((mutation) => publishToolMutation(mutation));
      }
      appendActivity('completed', 'Réponse générée', data.message?.slice(0, 160) || '', { status: 'success' });
      setMessages((prev) => [
        ...prev,
        {
          content: data.message || 'Pas de réponse disponible.',
          role: 'assistant',
          agent: data.agent || 'nexus',
        },
      ]);
      setCurrentFocus('');
      setLoading(false);
      clearPendingConfirmation();
      return;
    }

    if (type === 'error') {
      appendActivity('error', 'Erreur rencontrée', data.message, { status: 'error' });
      clearPendingConfirmation();
      setMessages((prev) => [
        ...prev,
        {
          content: data.message || "Désolé, une erreur est survenue.",
          role: 'assistant',
          agent: 'nexus',
          isError: true,
        },
      ]);
      setCurrentFocus('');
      setLoading(false);
      return;
    }
  }, [appendActivity, clearPendingConfirmation, publishToolMutation]);

  const streamMessage = useCallback(async (rawMessage, options = {}) => {
    const { force = false, silent = false } = options;
    const message = rawMessage.trim();
    if (!message || (!force && loading)) return;

    clearPendingConfirmation();

    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {});
      readerRef.current = null;
    }

    if (!silent) {
      setMessages((prev) => [...prev, { content: message, role: 'user' }]);
      appendActivity('start', 'Demande envoyée à Nexus', message, { status: 'running' });
      setCurrentFocus('Initialisation du routage Nexus');
    } else {
      appendActivity('start', 'Reprise du traitement Nexus', '', { status: 'running' });
      setCurrentFocus('Confirmation en cours...');
    }

    setLoading(true);

    try {
      const apiUrl = `${apiService.getBaseUrl()}/chat/stream`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apiService.getHeaders(),
        },
        body: JSON.stringify({
          message,
          conversation_id: conversationIdRef.current,
          page_context: pageContext,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error("Impossible d'atteindre le service de chat.");
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');

        while (boundary !== -1) {
          const chunk = buffer.slice(0, boundary).trim();
          buffer = buffer.slice(boundary + 2);
          if (chunk.startsWith('data:')) {
            const payload = chunk.replace(/^data:\s*/, '');
            if (payload !== '[DONE]') {
              try {
                const event = JSON.parse(payload);
                handleStreamEvent(event);
              } catch (error) {
                console.warn('Échec du parsing SSE:', error);
              }
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (error) {
      console.error('Erreur streaming Nexus:', error);
      appendActivity('error', 'Erreur réseau', error.message, { status: 'error' });
      setMessages((prev) => [
        ...prev,
        {
          content: "Désolé, je n'ai pas pu contacter le service. Réessaie dans un instant.",
          role: 'assistant',
          agent: 'nexus',
          isError: true,
        },
      ]);
      setLoading(false);
      setCurrentFocus('');
    } finally {
      if (readerRef.current) {
        readerRef.current.cancel().catch(() => {});
        readerRef.current = null;
      }
    }
  }, [appendActivity, clearPendingConfirmation, handleStreamEvent, loading, pageContext]);

  const startBugReportFlow = useCallback(async () => {
    if (bugReportInitiating || loading) return;
    setBugReportInitiating(true);
    pushAssistantMessage(
      'Je lance le mode reporter de bug. Nexus va te poser les questions nécessaires.'
    );
    try {
      const projectId = await ensureDeepflowProjectId();
      const conversationLog = buildConversationLog();
      const activitySummary = buildActivityLogSummary();

      const userIdentity = user?.name || user?.email || 'Utilisateur Nexus';
      const instructions = [
        'Raccourci déclenché : Reporter un bug.',
        'Objectif : Tu prends entièrement la main sur la discussion pour qualifier le bug, proposer un titre clair et créer un objectif dans le projet Deepflow.',
        '',
        'Procédure attendue :',
        '1. Demande d’abord si le bug concerne Deepflow (site/app) ou Nexus (IA).',
        "2. Pose les questions nécessaires (contexte, reproduction, impact, environnement) jusqu'à avoir assez d'information.",
        '3. Reformule la description en français clair et propose un titre court et percutant.',
        `4. Crée un objectif dans le projet Deepflow (ID ${projectId}) via l’outil objectives__create en respectant ce format de nom : "[BUG] [NEXUS|DEEPFLOW] {titre}". Le workflow bug attribue automatiquement l'objectif a l'employe #1 (owner_id: 1) et abonne le reporteur, pas besoin de le faire manuellement.`,
        '5. Dans la description de l’objectif, inclue : origine (Deepflow ou Nexus), auteur du signalement, date, description reformulée, étapes clés et impact.',
        "6. Si le bug concerne Nexus, ajoute OBLIGATOIREMENT dans la description un bloc '--- Historique conversation ---' avec le log ci-dessous, puis un bloc '--- Journal MCP ---' avec le journal ci-dessous.",
        '7. Confirme à l’utilisateur quand le ticket est créé.',
        '',
        `Identité de l’utilisateur : ${userIdentity}`,
        '',
        'Historique de conversation actuel (à inclure uniquement pour un bug Nexus) :',
        conversationLog || 'Aucun échange exploitable.',
        '',
        'Journal MCP actuel (à inclure uniquement pour un bug Nexus) :',
        activitySummary || 'Aucun appel MCP enregistré.',
        '',
        'Ajoute aussi les échanges et appels qui auront lieu après cette consigne si le bug concerne Nexus.',
      ].join('\n');

      await streamMessage(instructions, { force: true, silent: true });
    } catch (error) {
      console.error('Bug report trigger failed', error);
      pushAssistantMessage("Impossible de lancer le mode bug reporter pour l'instant.");
    } finally {
      setBugReportInitiating(false);
    }
  }, [
    bugReportInitiating,
    loading,
    pushAssistantMessage,
    ensureDeepflowProjectId,
    buildConversationLog,
    buildActivityLogSummary,
    streamMessage,
    user,
  ]);

  const bugShortcutDisabled = bugReportInitiating || loading;

  const lockConfirmationMessage = (batchId) => {
    if (!batchId) return;
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.type === 'confirmation' && msg.confirmation?.batchId === batchId) {
          return {
            ...msg,
            confirmation: { ...msg.confirmation, locked: true },
          };
        }
        return msg;
      })
    );
  };

  const handleConfirmation = (accepted, confirmationFromCard = null) => {
    const confirmation = pendingConfirmation || confirmationFromCard;
    if (!confirmation) return;
    const reply = accepted ? 'oui' : 'non';
    lockConfirmationMessage(confirmation.batchId);
    const lockedConfirmation = { ...confirmation, locked: true };
    setPendingConfirmation(lockedConfirmation);
    pendingConfirmationRef.current = lockedConfirmation;
    streamMessage(reply, { force: true, silent: true });
  };

  const handleProactivityDecision = useCallback(
    async (request, decision) => {
      if (!request || !decision) {
        return;
      }
      setRespondingProactivityId(request.id);
      try {
        await apiService.respondToNexusProactivityRequest(request.id, decision);
        setProactivityRequests((prev) => prev.filter((item) => item.id !== request.id));
        if (decision === 'accept') {
          pushAssistantMessage(
            `Parfait, je clos l'objectif « ${request.objective_name} ».`,
          );
        } else {
          pushAssistantMessage(`Très bien, on garde « ${request.objective_name} » ouvert pour l'instant.`);
        }
      } catch (error) {
        console.error('Impossible de traiter la demande Nexus:', error);
        pushAssistantMessage("Oups, je n'ai pas pu traiter cette demande. Réessaie dans un instant.");
      } finally {
        setRespondingProactivityId(null);
        loadProactivityRequests({ silent: true });
      }
    },
    [loadProactivityRequests, pushAssistantMessage]
  );

  const handleClearConversation = async () => {
    if (clearingConversation) return;

    if (readerRef.current) {
      readerRef.current.cancel().catch(() => {});
      readerRef.current = null;
    }

    clearPendingConfirmation();
    pendingConfirmationRef.current = null;
    setLoading(false);
    setCurrentFocus('');
    setActivityLog([]);
    setMessages([getRandomGreeting()]);
    setBugReportInitiating(false);

    const previousConversationId = conversationIdRef.current;
    setClearingConversation(true);
    try {
      await apiService.post('/chat/reset', { conversation_id: previousConversationId });
    } catch (error) {
      console.warn('Échec de la réinitialisation de la conversation Nexus:', error);
    } finally {
      setClearingConversation(false);
    }

    const newConversationId = generateConversationId();
    setConversationId(newConversationId);
  };

  const activityButtonLabel = showActivityPanel ? 'Masquer les outils' : 'Voir les outils';
  const clearConversationLabel = clearingConversation ? 'Réinitialisation…' : 'Nouvelle conversation';
  const pendingAlertCount = proactivityRequests.length + automationUnread;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="relative flex h-14 w-14 items-center justify-center rounded-full border border-indigo-200 bg-white shadow-xl ring-4 ring-indigo-500/10 transition-all duration-300 hover:scale-105 hover:bg-indigo-50"
        >
          <img src="/Nexus_icon.png" alt="Nexus" className="h-8 w-8" />
          {nexusAlertPending && pendingAlertCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
              {Math.min(99, pendingAlertCount)}
            </span>
          )}
        </button>
      )}

      {isOpen && (
        <div className="absolute bottom-2 right-2 h-[30rem] w-[44rem] max-w-[calc(100vw-1rem)] overflow-hidden rounded-3xl border border-indigo-100/70 bg-white/90 shadow-2xl backdrop-blur-xl">
          <div className="flex h-full flex-col">
            <header className="rounded-t-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-500 px-4 py-3 text-white shadow-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/20 backdrop-blur">
                    <img src="/Nexus_icon.png" alt="Nexus" className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold leading-6">Nexus</h3>
                    <p className="text-xs text-indigo-100">
                      Assistant connecté aux serveurs MCP.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={startBugReportFlow}
                    disabled={bugShortcutDisabled}
                    className="rounded-full border border-white/40 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {bugReportInitiating ? 'Préparation…' : 'Reporter un bug'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowActivityPanel((prev) => !prev)}
                    className="rounded-full border border-white/30 bg-white/10 p-2 text-white transition hover:bg-white/20"
                    aria-label={activityButtonLabel}
                    title={activityButtonLabel}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.25 7A2.25 2.25 0 017.5 4.75h8.19L19.25 8.3v8.7a2.25 2.25 0 01-2.25 2.25H7.5a2.25 2.25 0 01-2.25-2.25V7z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.75 10.75h6.5M8.75 14.25h4" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleClearConversation}
                    disabled={clearingConversation}
                    className="rounded-full border border-white/40 bg-white/10 p-2 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label={clearConversationLabel}
                    title={clearConversationLabel}
                  >
                    {clearingConversation ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle className="opacity-40" cx="12" cy="12" r="8" strokeWidth={1.5} />
                        <path className="opacity-90" strokeLinecap="round" strokeWidth={1.5} d="M12 3.5a8.5 8.5 0 018.5 8.5" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 12h7.5m-3.75-3.75V15.75" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.25 7.25A2 2 0 017.25 5.25h9.5l2 2v7a2 2 0 01-2 2h-5.5l-4 3.5v-3.5h-2a2 2 0 01-2-2v-7z" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="rounded-full border border-white/40 bg-white/10 p-1 text-white transition hover:bg-white/20"
                    aria-label="Fermer le chat"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </header>

            <div
              className={`grid min-h-0 flex-1 grid-cols-1 ${
                showActivityPanel ? 'lg:grid-cols-[2fr_1fr]' : ''
              }`}
            >
              <div className="flex min-h-0 flex-col">
                <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 text-[0.95rem] leading-relaxed space-y-4">
                  {messages.map((msg, idx) => {
                    if (msg.type === 'confirmation' && msg.confirmation) {
                      const isForeignCard =
                        pendingConfirmation &&
                        pendingConfirmation.batchId !== msg.confirmation.batchId;
                      return (
                        <div key={`confirm-${idx}`} className="flex items-start gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm">
                            <img src="/Nexus_icon.png" alt="Nexus" className="h-7 w-7" />
                          </div>
                          <ConfirmationPrompt
                            prompt={msg.confirmation}
                            onConfirm={() => handleConfirmation(true, msg.confirmation)}
                            onCancel={() => handleConfirmation(false, msg.confirmation)}
                            disabled={Boolean(isForeignCard)}
                          />
                        </div>
                      );
                    }
                    return <ChatBubble key={`msg-${idx}`} message={msg} />;
                  })}

                  {proactivityLoading && proactivityRequests.length === 0 && (
                    <div className="flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-indigo-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm font-medium">Je vérifie si des actions Nexus t'attendent…</span>
                    </div>
                  )}

                  {proactivityRequests.map((request) => (
                    <div key={`proactivity-${request.id}`} className="flex items-start gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm">
                        <img src="/Nexus_icon.png" alt="Nexus" className="h-7 w-7" />
                      </div>
                      <ProactivityPrompt
                        request={request}
                        onAccept={() => handleProactivityDecision(request, 'accept')}
                        onDecline={() => handleProactivityDecision(request, 'decline')}
                        loading={respondingProactivityId === request.id}
                      />
                    </div>
                  ))}

                  {loading && <TypingIndicator focus={currentFocus} entries={activityLog} />}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              <aside
                className={`${
                  showActivityPanel ? 'lg:flex' : 'lg:hidden'
                } hidden min-h-0 border-l border-indigo-100/80 bg-indigo-50/50 px-4 py-4 lg:flex-col`}
              >
                <div className="mb-4 flex shrink-0 items-center justify-between">
                  <span className="text-sm font-semibold text-indigo-900">Outils Nexus</span>
                  <span className="text-xs text-indigo-500">
                    {activityLog.length} {activityLog.length > 1 ? 'événements' : 'événement'}
                  </span>
                </div>
                <div className="flex-1 overflow-y-auto pr-1">
                  <ActivityTimeline entries={activityLog} />
                </div>
              </aside>
            </div>

            <ChatInput onSendMessage={streamMessage} isLoading={loading} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
